import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShieldCheck, Leaf, Clock, Dna, Store, Bell } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Seo from "@/components/Seo";
import { productImage, onImgError } from "@/lib/image";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [optIdx, setOptIdx] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [wlEmail, setWlEmail] = useState("");
  const [wlJoining, setWlJoining] = useState(false);
  const [wlJoined, setWlJoined] = useState(false);

  useEffect(() => {
    setProduct(null);
    setQty(1);
    setOptIdx(0);
    setWlEmail("");
    setWlJoined(false);
    api
      .get(`/product/${id}`.replace("/product/", "/products/"))
      .then(({ data }) => setProduct(data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="max-w-7xl mx-auto px-6 py-32 text-center">
        <p className="text-slate-400">Product not found.</p>
        <Link to="/shop" className="text-emerald-400 mt-4 inline-block">Back to shop</Link>
      </div>
    );

  if (!product)
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-12">
        <div className="aspect-square bg-[#0F0F0F] animate-pulse" />
        <div className="space-y-4">
          <div className="h-10 w-2/3 bg-[#0F0F0F] animate-pulse" />
          <div className="h-24 bg-[#0F0F0F] animate-pulse" />
        </div>
      </div>
    );

  const specs = [
    { icon: Leaf, label: "THC", value: product.thc },
    { icon: Clock, label: "Flowering", value: product.flowering_time },
    { icon: Dna, label: "Type", value: product.strain_type },
    { icon: ShieldCheck, label: "Delta-9", value: product.cbd },
  ];

  const options = Array.isArray(product.options) && product.options.length ? product.options : null;
  const activePrice = options ? options[optIdx].price : product.price;

  const stock = typeof product.stock === "number" ? product.stock : null;
  const isClone = product.category === "clones";
  const isPreorder = isClone && (product.availability || "preorder") === "preorder";
  const capped = isPreorder && product.preorder_capped === true;
  const soldOut = !isPreorder && stock !== null && stock <= 0;
  const lowStock = !isPreorder && stock !== null && stock > 0 && stock <= 5;

  const handleAdd = () => {
    if (soldOut || capped) return;
    if (options) {
      const opt = options[optIdx];
      addItem(product, qty, {
        id: `${product.id}:${opt.quantity}`,
        name: `${product.name} — ${opt.label}`,
        price: opt.price,
      });
    } else {
      addItem(product, qty);
    }
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    const email = wlEmail.trim();
    if (!email) return;
    setWlJoining(true);
    try {
      const { data } = await api.post("/waitlist", { product_id: product.id, email });
      setWlJoined(true);
      toast.success(data.message || "You're on the list!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not join the waitlist.");
    } finally {
      setWlJoining(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
      <Seo
        title={`${product.name} — ${product.category === "clones" ? "Cannabis Clone" : "Regular Seeds"} | Dark Growers Genetics`}
        description={`${product.name} (${product.lineage || product.genetics || product.strain_type}). ${product.category === "clones" ? "Lab-tested rooted clone" : "Regular seeds, packs of 6 or 12"} from Dark Growers Genetics. Free next-day shipping.`}
        canonical={`https://darkgrowers.com/product/${product.id}`}
      />
      <button
        onClick={() => navigate(-1)}
        data-testid="back-button"
        className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-colors"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
        <div className="relative aspect-square bg-black border border-white/5">
          <img src={productImage(product.image_url)} onError={onImgError} alt={product.name} className="w-full h-full object-cover" />
          <span className="absolute top-4 left-4 label-caps text-[0.65rem] bg-black/60 backdrop-blur px-3 py-1.5 text-emerald-400 border border-emerald-500/20">
            {product.category === "clones" ? "Clone" : "Seeds"}
          </span>
        </div>

        <div>
          <p className="label-caps text-emerald-500 mb-3">{product.breeder || product.genetics}</p>
          <h1 data-testid="product-title" className="font-display text-5xl md:text-6xl text-slate-100 leading-none">
            {product.name}
          </h1>
          <p data-testid="product-detail-price" className="text-3xl text-slate-100 mt-5 font-light">
            ${activePrice.toFixed(2)}
          </p>
          <p className="text-slate-400 text-sm mt-2">{product.pack_size}</p>

          {isPreorder ? (
            <div className="mt-3" data-testid="detail-stock-status">
              {capped ? (
                <>
                  <span className="inline-block label-caps text-[0.65rem] bg-red-500/15 border border-red-500/40 text-red-300 px-3 py-1.5">
                    Fully Booked This Cycle
                  </span>
                  <p className="text-slate-400 text-sm leading-relaxed mt-3 border-l-2 border-red-500/50 pl-3">
                    This cut has hit its preorder cap for the current cycle. Join the waitlist below and we'll email you the moment it's rooted and ready to ship.
                  </p>
                </>
              ) : (
                <>
                  <span className="inline-block label-caps text-[0.65rem] bg-amber-500/15 border border-amber-500/40 text-amber-300 px-3 py-1.5">
                    Available for Preorder
                  </span>
                  <p className="text-slate-400 text-sm leading-relaxed mt-3 border-l-2 border-amber-500/50 pl-3">
                    This cut is available made-to-order. Once your payment is received, we cut and root your clone within 2 weeks, then ship UPS Next Day the moment it's ready.
                  </p>
                </>
              )}
            </div>
          ) : soldOut ? (
            <p data-testid="detail-stock-status" className="inline-block mt-3 label-caps text-[0.65rem] bg-red-500/15 border border-red-500/40 text-red-300 px-3 py-1.5">
              Sold Out — check back soon
            </p>
          ) : lowStock ? (
            <p data-testid="detail-stock-status" className="inline-block mt-3 label-caps text-[0.65rem] bg-amber-500/15 border border-amber-500/40 text-amber-300 px-3 py-1.5">
              Only {stock} left in stock
            </p>
          ) : isClone ? (
            <p data-testid="detail-stock-status" className="inline-block mt-3 label-caps text-[0.65rem] bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 px-3 py-1.5">
              Ready to Ship — ships next business day
            </p>
          ) : null}

          <p className="text-slate-300/90 text-sm leading-relaxed mt-7">{product.description}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5 border border-white/5 mt-8">
            <div className="bg-[#0F0F0F] p-4" data-testid="spec-lineage">
              <Dna size={16} className="text-emerald-500 mb-2" />
              <p className="label-caps text-slate-500 text-[0.6rem]">Lineage</p>
              <p className="text-slate-200 text-sm mt-1">{product.lineage || product.genetics || "—"}</p>
            </div>
            <div className="bg-[#0F0F0F] p-4" data-testid="spec-breeder">
              <Store size={16} className="text-emerald-500 mb-2" />
              <p className="label-caps text-slate-500 text-[0.6rem]">Breeder</p>
              <p className="text-slate-200 text-sm mt-1">{product.breeder || "—"}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-px bg-white/5 border border-white/5 mt-px">
            {specs.map((s) => (
              <div key={s.label} className="bg-[#0F0F0F] p-4">
                <s.icon size={16} className="text-emerald-500 mb-2" />
                <p className="label-caps text-slate-500 text-[0.6rem]">{s.label}</p>
                <p className="text-slate-200 text-sm mt-1 capitalize">{s.value || "—"}</p>
              </div>
            ))}
          </div>

          {options && (
            <div className="mt-8" data-testid="seed-options">
              <p className="label-caps text-slate-500 mb-3">Choose your pack</p>
              <div className="grid grid-cols-2 gap-3">
                {options.map((o, i) => (
                  <button
                    key={o.quantity}
                    data-testid={`seed-option-${o.quantity}`}
                    onClick={() => setOptIdx(i)}
                    className={`border p-4 text-left transition-colors ${
                      optIdx === i
                        ? "border-emerald-500 bg-emerald-500/10"
                        : "border-white/15 hover:border-white/40"
                    }`}
                  >
                    <p className="text-slate-100 text-sm">{o.label}</p>
                    <p className="text-emerald-400 text-lg mt-1">${o.price.toFixed(2)}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 mt-8">
            <div className="flex items-center border border-white/15">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="qty-dec"
                className="w-11 h-12 flex items-center justify-center hover:text-emerald-400 transition-colors"
              >
                <Minus size={15} />
              </button>
              <span className="w-10 text-center text-slate-100" data-testid="qty-value">{qty}</span>
              <button
                onClick={() => setQty((q) => q + 1)}
                data-testid="qty-inc"
                className="w-11 h-12 flex items-center justify-center hover:text-emerald-400 transition-colors"
              >
                <Plus size={15} />
              </button>
            </div>
            <button
              data-testid="detail-add-to-cart"
              onClick={handleAdd}
              disabled={soldOut || capped}
              className="flex-1 bg-emerald-600 text-white hover:bg-emerald-500 transition-colors font-medium py-3.5 text-sm tracking-wide disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-600"
            >
              {soldOut ? "Sold Out" : capped ? "Fully Booked — Join Waitlist Below" : isPreorder ? `Preorder — $${(activePrice * qty).toFixed(2)}` : `Add to Cart — $${(activePrice * qty).toFixed(2)}`}
            </button>
          </div>

          {isPreorder && (
            <div className="mt-8 border border-emerald-500/25 bg-emerald-500/[0.05] p-6" data-testid="waitlist-box">
              <p className="label-caps text-emerald-400 mb-1 flex items-center gap-2">
                <Bell size={15} /> Notify me when rooted
              </p>
              <p className="text-slate-400 text-xs leading-relaxed mb-4">
                Drop your email and we'll message you the moment <b className="text-slate-200">{product.name}</b> is rooted and ready to ship.
              </p>
              {wlJoined ? (
                <p data-testid="waitlist-success" className="text-sm text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
                  You're on the list — we'll email you the moment it's rooted. 🌱
                </p>
              ) : (
                <form onSubmit={joinWaitlist} className="flex gap-2">
                  <input
                    type="email"
                    required
                    data-testid="waitlist-email"
                    value={wlEmail}
                    onChange={(e) => setWlEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="flex-1 bg-[#050505] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-4 py-3 transition-colors placeholder:text-slate-600"
                  />
                  <button
                    type="submit"
                    disabled={wlJoining}
                    data-testid="waitlist-submit"
                    className="bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 transition-colors font-medium px-6 py-3 text-sm whitespace-nowrap"
                  >
                    {wlJoining ? "Adding…" : "Notify Me"}
                  </button>
                </form>
              )}
            </div>
          )}

          <div className="flex items-start gap-2 mt-8 border-t border-white/10 pt-6">
            <ShieldCheck size={16} className="text-emerald-500 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-500 leading-relaxed">
              Lab-tested clean for HLVD, TMV, CMV, TSWV & INSV. All products contain less than 0.3%
              Delta-9 THC and ship in discreet packaging. Certificate of Analysis available on request.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
